import {
  CrossClientAction,
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryExecutionProgressResponse,
  CrossClientQueryPhase,
  CrossClientQueryResult,
  CrossClientQueryResultStatus,
} from '../../models/CrossClient'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { createApi } from '@reduxjs/toolkit/query/react'

const _AI_MANAGEMENT_BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

interface GetCrossClientQueryExecutionResultsRequest extends BaseRequest {
  executionId: string
  updatedAfter?: string | null
}

interface GetAllCrossClientQueryExecutionsRequest extends BaseRequest {
  phases?: CrossClientQueryPhase[]
}

interface GetCrossClientQueryExecutionsRequest extends BasePageRequest {
  phases?: CrossClientQueryPhase[]
}

interface GetCrossClientQueryExecutionRequest extends BaseRequest {
  executionId: string
}

interface DeleteCrossClientQueryExecutionRequest extends BaseRequest {
  executionId: string
}
interface GetAllCrossClientActionsRequest extends BaseRequest {
  executionId: string
  updatedAfter?: string | null
}

interface UpdateCrossClientIntegrationEntitiesRequest extends BaseRequest {
  executionId: string
  connector: string
  relatedToType: string
  sourcePlatform: string
  sourceObject: string
  entityType: string
  entity: Record<string, unknown>
  targets: { externalId: string; clientId: string }[]
}

export const crossClientApi = createApi({
  reducerPath: 'crossClientApi',
  baseQuery: baseQueryWithTokenRefresh(_AI_MANAGEMENT_BASE_URL),
  endpoints: (builder) => ({
    getAllCrossClientQueryExecutions: builder.query<BaseResponse<CrossClientQueryExecution[]>, GetAllCrossClientQueryExecutionsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryExecution>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions`,
            params: { ...(request.phases && { phases: request.phases.join() }) },
          },
          fetchWithBQ,
        )
      },
    }),
    getCrossClientQueryExecutions: builder.query<BasePageResponse<CrossClientQueryExecution[]>, GetCrossClientQueryExecutionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.phases && { phases: request.phases.join() }),
          },
        }
      },
    }),
    getCrossClientQueryExecution: builder.query<CrossClientQueryExecution | null, GetCrossClientQueryExecutionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: { data: CrossClientQueryExecution } | undefined) => {
        return response?.data ?? null
      },
    }),
    getAllCrossClientQueryExecutionResults: builder.query<BaseResponse<CrossClientQueryResult[]>, GetCrossClientQueryExecutionResultsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryResult>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/results`,
            params: { ...(request.updatedAfter && { updatedAfter: request.updatedAfter }) },
          },
          fetchWithBQ,
        )
      },
    }),
    deleteCrossClientQueryExecution: builder.mutation<null, DeleteCrossClientQueryExecutionRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client/executions/${executionId}`,
        method: 'DELETE',
      }),
    }),
    getAllCrossClientActions: builder.query<BaseResponse<CrossClientAction[]>, GetAllCrossClientActionsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientAction>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/actions`,
            params: { ...(request.updatedAfter && { updatedAfter: request.updatedAfter }) },
          },
          fetchWithBQ,
        )
      },
    }),
    updateCrossClientIntegrationEntities: builder.mutation<void, UpdateCrossClientIntegrationEntitiesRequest>({
      query: ({ accountId, ...body }) => ({
        url: `/accounts/${accountId}/cross-client/update-integration-entities`,
        method: 'POST',
        body,
      }),
    }),
    getCrossClientQueryExecutionProgress: builder.query<CrossClientQueryExecutionProgress | null, GetCrossClientQueryExecutionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/progress`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: { data: CrossClientQueryExecutionProgressResponse[] }) => {
        if (!response.data?.length) {
          return null
        }
        return {
          executionId: response.data[0].executionId,
          phase: response.data[0].phase,
          startedAt: response.data[0].startedAt,
          finishedAt: response.data[0].finishedAt,
          canceledAt: response.data[0].canceledAt,
          lastUpdatedAt: response.data[0].lastUpdatedAt,
          statuses: response.data.reduce(
            (record, response) => {
              record[response.status] = response.resultCount
              return record
            },
            {} as Record<CrossClientQueryResultStatus, number>,
          ),
        }
      },
    }),
  }),
})

export const {
  useLazyGetCrossClientQueryExecutionsQuery,
  useLazyGetCrossClientQueryExecutionQuery,
  useLazyGetAllCrossClientQueryExecutionsQuery,
  useLazyGetAllCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientQueryExecutionProgressQuery,
  useDeleteCrossClientQueryExecutionMutation,
  useLazyGetAllCrossClientActionsQuery,
  useUpdateCrossClientIntegrationEntitiesMutation,
} = crossClientApi
